import { EntityId, StoreId } from '../../config/store-config';

// One financial row per marketplace order, normalized across the three
// platforms. Keyed (storeId, orderSn) — the same key the billing-request
// collector uses — so these rows join to issued invoices without a mapping
// table.
//
// The three platforms disagree about almost everything: Shopee reports per
// order, Lazada reports transaction *lines* keyed by fee name, TikTok reports
// per settlement statement. This shape is the agreement.

export type FinancePlatform = 'lazada' | 'shopee' | 'tiktok';

// Which date assigns a row to a reporting period. Carried as a read-time
// choice rather than a re-fetch, because all three are stored on every row.
export type PeriodBasis = 'orderDate' | 'taxPointDate' | 'settlementDate';

// How completely the platform answered. Not every store yields the same
// detail — see FINANCE_COVERAGE below — and a total is misleading if the
// reader cannot tell a real zero from an unavailable figure.
export type FinanceDetailLevel =
    // Per-order, with every fee broken out by type. Shopee today.
    | 'order-fees'
    // Per-order totals, but fees only as a single lump. Lazada via
    // payout/status until the Finance API permission is granted.
    | 'order-total'
    // Statement-level only; rows are a whole payout, not one order.
    | 'statement';

export interface FinanceGross {
    // What the items sold for, before any discount.
    itemsBeforeDiscount: number;
    // Seller-funded. REDUCES the VAT tax base.
    sellerDiscount: number;
    // Platform-funded voucher/subsidy. You still receive the money, so it
    // generally STAYS IN the tax base. Kept apart from sellerDiscount and
    // never netted against it — plan §4.6 calls conflating these "the most
    // common source of understated output VAT for marketplace sellers".
    platformSubsidy: number;
    // Delivery charged to the buyer. Taxable consideration in principle
    // (plan §4.7) — but see the note on `vatBase` below.
    buyerShippingPaid: number;
    // Everything the buyer actually paid, shipping included.
    buyerTotal: number;
    // What the seller's supply is worth: items after discounts, excluding
    // shipping that passes straight through to the carrier. This is the
    // figure that ties to escrow/settlement arithmetic.
    sellerRevenue: number;
    // THE VAT BASE, VAT included: what the seller is paid for the supply.
    // Item price less SELLER-funded discounts, plus shipping the buyer paid.
    // Platform-funded vouchers, coins and payment promotions stay in — the
    // platform tops the seller up for them. buyerTotal is already net of
    // those, so splitting VAT from it under-declared (3.4% on disorn's 2026
    // rows). Also the revenue the ฿1.8M threshold counts. See
    // vat-regis-plan/MARKETPLACE-REVENUE-TAX-ASSESSMENT.md §2.1.
    taxableConsideration: number;
}

// Platform charges, positive numbers being costs to the seller. Most carry
// 7% VAT that becomes claimable input tax once the owning entity is
// registered (plan §3.5, §4.9) — but not all: see vatBearingFeeTotal below
// for which buckets the platform actually invoices with VAT.
export interface FinanceFees {
    commission: number;
    transaction: number;
    service: number;
    affiliate: number;
    // Net shipping borne by the seller: actual cost less what the buyer paid
    // and less any platform rebate. Negative means shipping earned money.
    shippingBorne: number;
    other: number;
    total: number;
}

export interface FinanceOrderRecord {
    // `${storeId}:${orderSn}` on Shopee, which settles an order once;
    // `${storeId}:${orderSn}:${part}` on Lazada and TikTok — see `part`.
    _id: string;
    storeId: StoreId;
    platform: FinancePlatform;
    // Snapshotted at first sync and never overwritten on re-sync: a later
    // change of a store's owner in config must not retroactively restamp
    // history. A re-sync that resolves a different entity warns instead.
    entityId: EntityId;
    orderSn: string;
    // Which settlement slice of the order this document holds: the ledger
    // month (YYYY-MM) on Lazada, the statement id on TikTok, null on Shopee.
    //
    // One document per ORDER was wrong for the two platforms that settle an
    // order in pieces. A sync covers one period, and a record built from
    // that period's lines replaced the whole order on upsert: February's
    // ฿5.46 affiliate fee on a January sale overwrote the ฿85 sale, and a
    // TikTok refund statement overwrote the sale statement before it.
    // Keyed per slice, every payout keeps its own document; a settlement
    // view sees each in the month it was paid, and a tax-point view — every
    // slice carries the order's creation date — sums them back to the
    // whole order. `orderSn` still names the order, so joins to billing
    // requests stay on (storeId, orderSn).
    part: string | null;
    currency: string;
    detailLevel: FinanceDetailLevel;

    // Epoch seconds. null where the platform does not report it, or where a
    // join to the order API missed (the sync counts those as missingTaxPoint).
    orderDate: number | null;
    // ORDER CREATION on every platform (commit e322946), not courier
    // hand-off. §78's tax point is the earliest of delivery, ownership
    // transfer, payment or invoice, and creation precedes all four, so it
    // is never late. See normalize-shopee.ts for the full reasoning.
    taxPointDate: number | null;
    settlementDate: number | null; // payout released

    gross: FinanceGross;
    fees: FinanceFees;
    adjustments: { refund: number; compensation: number; other: number };
    // What the platform actually released. Ties to the bank.
    netSettlement: number;

    // null — NOT zero — when the owning entity was not VAT registered at the
    // tax point. A zero reads as "no VAT due on this sale"; a null reads as
    // "this is not a VAT sale at all", which is the true state for every
    // `pensri` row and for every `disorn` row before the ภ.พ.20 date.
    vat: { base: number; output: number } | null;
    // Claimable input tax on the platform's fees, same nullability rule.
    feeVat: { base: number; input: number } | null;

    // Retained because RD cross-checks marketplace data (plan §6) and platform
    // retention windows expire. Also lets a fee misclassification be
    // re-derived without re-fetching.
    raw: { source: string; fetchedAt: number; payload: unknown };
    syncedAt: number;
}

// What each platform actually yields, as measured against the live APIs on
// 2026-08-05 — not as documented. All three reach order level, but by
// noticeably different routes, and each has one trap worth recording:
//
//   shopee — payment/get_escrow_list (settlement index, ~15-day window) then
//            payment/get_escrow_detail_batch (≤50 order_sn, POST). Richest
//            breakdown of the three. Dates come from order/get_order_detail.
//
//   lazada — /finance/transaction/details/get — note the PLURAL "details".
//            The singular path exists and returns InsufficientPermission
//            ("App does not have permission to access this api") on both apps
//            regardless of what is granted, which reads exactly like a missing
//            permission and is not one. Returns one row per fee per order
//            item, pivoted into records by order_no. Verified working on both
//            /gen4group/ and /arduino4/ credentials.
//
//   tiktok — /finance/202309/statements then
//            .../statements/{id}/statement_transactions. `sort_field` is
//            REQUIRED on the transactions call and its absence returns a 400
//            (code 36009004), not a validation hint.
//
// Surfaced in the UI so a store whose fees are partly unattributed is never
// silently averaged in with one reporting every fee by type.
export const FINANCE_COVERAGE: Record<FinancePlatform, FinanceDetailLevel> = {
    shopee: 'order-fees',
    lazada: 'order-fees',
    tiktok: 'order-fees',
};

export const financeRecordId = (
    storeId: StoreId,
    orderSn: string,
    part: string | null = null,
): string =>
    part === null ? `${storeId}:${orderSn}` : `${storeId}:${orderSn}:${part}`;

export const emptyFees = (): FinanceFees => ({
    commission: 0,
    transaction: 0,
    service: 0,
    affiliate: 0,
    shippingBorne: 0,
    other: 0,
    total: 0,
});

// Round to satang. Every figure that lands on a record goes through this.
//
// Not defensive tidying — binary floats genuinely cannot hold these values.
// The Lazada fixture's three fees, 17.39 + 5.39 + 8.35, sum to
// 31.130000000000003, and that residue then fails to tie against a VAT base
// computed from the same total. Rounding at the boundary rather than at
// display time is what keeps `base + vat === gross` and
// `revenue − fees === settlement` true as identities instead of approximately
// true, which is the whole basis for trusting the reconciliation checks.
export const round2 = (value: number): number =>
    Math.round((value + Number.EPSILON) * 100) / 100;

// The part of a record's fees that the platform invoices WITH 7% VAT, from
// which input tax may be estimated when the platform does not state it
// (assessment §1.3, §2.3). Left out:
//
//   shippingBorne — domestic carriage, VAT-exempt (§81(1)(ณ)); Shopee bills
//                   SPX shipping with a receipt and TikTok's logistics fee
//                   is invoiced by Thai Happy Logistic, not TikTok Shop.
//   other         — on Shopee, reverse / overseas-return shipping: carriage.
//   affiliate     — on TikTok only, paid to creators, who are mostly not
//                   registrants. Shopee's AMS is Shopee's own service and
//                   is VAT-bearing.
//
// TikTok's `other` is the unitemised remainder of fee_amount, which on the
// fixture is exactly transaction fee + Commerce Growth Fee + infrastructure
// fee — TikTok Shop's own VAT-bearing charges — so it stays in. Lazada never
// comes here: its ledger states VAT_in_amount per line and the normalizer
// carries it.
export const vatBearingFeeTotal = (
    record: Pick<FinanceOrderRecord, 'platform' | 'fees'>,
): number => {
    const { fees } = record;
    switch (record.platform) {
        case 'shopee':
            return round2(
                fees.commission +
                    fees.transaction +
                    fees.service +
                    fees.affiliate,
            );
        case 'tiktok':
            return round2(
                fees.commission + fees.transaction + fees.service + fees.other,
            );
        case 'lazada':
            return round2(fees.total);
    }
};

// Sums the buckets, rounding each before adding so the total cannot inherit
// drift the individual lines do not show.
export const sumFees = (fees: FinanceFees): number =>
    round2(
        round2(fees.commission) +
            round2(fees.transaction) +
            round2(fees.service) +
            round2(fees.affiliate) +
            round2(fees.shippingBorne) +
            round2(fees.other),
    );
